import { Box, Typography, Table, TableHead, TableRow, TableCell, TableBody, Alert, Stack, Divider, TableSortLabel } from "@mui/material";
import { useAdvisor } from "../context/AdvisorContext";
import { useDealContext } from "../../deal/context";
import { useAdvisorLedger } from "../hooks/useAdvisorLedger";
import Loading from "../../../components/Loading";
import { AdvisorTitle } from "../components/AdvisorTitle";
import { AdvisorTable, OverviewBox } from "../../company/styles/AdvisorStyles";
import dayjs from "dayjs";
import { DisplayBoxBase } from "../../../components/shared/StyledComponents"
import { Link } from "react-router-dom";
import { AdvisorActivityLedgerEntryStatus } from "../../../types";
import { useState } from "react";
import { FollowUpPrompts } from "../components/meetings/FollowupPrompts";

type SortField = 'date' | 'activity' | 'shares';
type SortOrder = 'asc' | 'desc';

export const AdvisorLedger = () => {
    const { advisor } = useAdvisor();
    const { dealContext } = useDealContext();
    const project = dealContext.project;
    const [sortField, setSortField] = useState<SortField>('date');
    const [sortOrder, setSortOrder] = useState<SortOrder>('desc');

    if (!advisor?.uuid) return <Loading />;
    const { ledger, loading, error } = useAdvisorLedger(advisor.uuid, project?.tag);

    if (loading) return <Loading />;
    if (error) return <Alert severity="error">{error.message || "Failed to load ledger"}</Alert>;
    if (!ledger) return <Alert severity="info">No ledger data found.</Alert>;

    const totalShares = ledger?.reduce((sum, r) => sum + (r.shares_earned || 0), 0);
    const acquiredShares = ledger?.filter(r => r.status === AdvisorActivityLedgerEntryStatus.ACQUIRED)?.reduce((sum, r) => sum + (r.shares_earned || 0), 0);
    const availableShares = ledger?.filter(r => r.status === AdvisorActivityLedgerEntryStatus.APPROVED)?.reduce((sum, r) => sum + (r.shares_earned || 0), 0);

    const handleSort = (field: SortField) => {
        if (field === sortField) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortOrder('asc');
        }
    };

    const sortedLedger = [...(ledger || [])].sort((a, b) => {
        const multiplier = sortOrder === 'asc' ? 1 : -1;
        
        switch (sortField) {
            case 'date':
                return multiplier * dayjs(b.approved_at ?? b.created_at).diff(dayjs(a.approved_at ?? a.created_at));
            case 'activity':
                return multiplier * a.activity.name.localeCompare(b.activity.name);
            case 'shares':
                return multiplier * ((a.shares_earned || 0) - (b.shares_earned || 0));
            default:
                return 0;
        }
    });

    return (
        <Box>
            <AdvisorTitle project={project} section="Advisor Shares" subheading={<>Earn and acquire advisor shares for helping the company succeed. <Link to="https://info.brightloop.com/en/articles/11101661-advisory-shares" target="_blank">Learn more.</Link></>} />
            <Box sx={{ display: "flex", flexDirection: "row", gap: "40px" }}>
                <Box sx={{ flex: "1 1" }}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortField === 'date'}
                                        direction={sortField === 'date' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('date')}
                                    >
                                        Date
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        active={sortField === 'activity'}
                                        direction={sortField === 'activity' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('activity')}
                                    >
                                        Activity
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">
                                    <TableSortLabel
                                        active={sortField === 'shares'}
                                        direction={sortField === 'shares' ? sortOrder : 'asc'}
                                        onClick={() => handleSort('shares')}
                                    >
                                        Shares
                                    </TableSortLabel>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedLedger.map(record => (
                                <TableRow key={record.id}>
                                    <TableCell>{dayjs(record.approved_at ?? record.created_at).format('MMM D, YYYY')}</TableCell>
                                    <TableCell>
                                        <Typography variant="body1" fontWeight={700}>{record.activity.name}</Typography>
                                        <Typography variant="body2" color="textSecondary">{record.activity.description}</Typography>
                                    </TableCell>
                                    <TableCell align="right"><Typography variant="body1" fontWeight={700}>{record.shares_earned} {record.shares_earned === 1 ? "share" : "shares"}</Typography></TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
                <Box sx={{ flex: "1 1", minWidth: "225px", maxWidth: "25%" }}>
                    <Stack direction="column" spacing={4}>
                        <OverviewBox className={`display-box white`} onClick={() => { }} sx={{ paddingLeft: 0, paddingRight: 0, borderColor: "primary.main" }}>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0, mb: 2, paddingLeft: "20px", paddingRight: "20px", borderBottom: "1px solid", borderBottomColor: "primary.main", pb: 2 }}>
                                <Typography variant="h5">
                                    <Link to="shares">{ledger?.reduce((acc, curr) => acc + (curr.shares_earned ?? 0), 0)}</Link>
                                </Typography>
                                <Typography variant="body2" color="textSecondary">Advisor Shares Earned</Typography>
                                <Typography variant="body2" color="textSecondary"><Link to="https://info.brightloop.com/en/articles/11101661-advisory-shares" target="_blank">How Advisor Shares Work &raquo;</Link></Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, paddingLeft: "20px", paddingRight: "20px" }}>
                                <Stack direction="column" spacing={0}>
                                    <Typography variant="h6" fontWeight={700} display="inline">{acquiredShares}</Typography>
                                    <Typography variant="body2" color="textSecondary" display="inline">Shares Acquired</Typography>
                                </Stack>
                                <Stack direction="column" spacing={0}>
                                    <Typography variant="h6" fontWeight={700} display="inline">{availableShares}</Typography>
                                    <Typography variant="body2" color="textSecondary" display="inline">Available at Next Issue Date</Typography>
                                </Stack>
                                <Stack direction="column" spacing={0}>
                                    <Typography variant="h6" fontWeight={700}>September 30, 2025</Typography>
                                    <Typography variant="body2" color="textSecondary">Next Issue Date</Typography>
                                </Stack>
                            </Box>
                        </OverviewBox>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, paddingLeft: "20px", paddingRight: "20px" }}>
                            <Typography variant="body2" fontWeight={700}>FAQ</Typography>
                            <Link to="https://info.brightloop.com/en/articles/11101661-advisory-shares#h_34d109d0ca" target="_blank" style={{ textDecoration: 'none' }}>
                                <Typography variant="body2" color="textSecondary">How are advisor shares earned?</Typography>
                            </Link>
                            <Link to="https://info.brightloop.com/en/articles/11101661-advisory-shares#h_bfa73cb99c" target="_blank" style={{ textDecoration: 'none' }}>
                                <Typography variant="body2" color="textSecondary">How are earned advisory shares acquired?</Typography>
                            </Link>
                            <Link to="https://info.brightloop.com/en/articles/11101661-advisory-shares#h_186b36b297" target="_blank" style={{ textDecoration: 'none' }}>
                                <Typography variant="body2" color="textSecondary">This sounds complex. Who manages it?</Typography>
                            </Link>
                            <Link to="https://info.brightloop.com/en/articles/11101661-advisory-shares#h_2143d2fbff" target="_blank" style={{ textDecoration: 'none' }}>
                                <Typography variant="body2" color="textSecondary">What shares am I earning?</Typography>
                            </Link>
                            <Link to="https://info.brightloop.com/en/articles/11101661-advisory-shares#h_6e563bdf7b" target="_blank" style={{ textDecoration: 'none' }}>
                                <Typography variant="body2" color="textSecondary">Do I actually own shares in the company?</Typography>
                            </Link>
                            <Link to="https://info.brightloop.com/en/articles/11101661-advisory-shares#h_95a2d7643b" target="_blank" style={{ textDecoration: 'none' }}>
                                <Typography variant="body2" color="textSecondary">Who can earn and acquire shares?</Typography>
                            </Link>
                            <Link to="https://info.brightloop.com/en/articles/11101661-advisory-shares" target="_blank">
                                <Typography variant="body2" color="primary.main">View more &raquo;</Typography>
                            </Link>
                        </Box>
                    </Stack>
                </Box>
            </Box>
        </Box>
    );
} 